import { useState, useEffect } from 'react'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import ModalListaClientes from '../components/ModalListaClientes'
import { clientesApi } from '../api/notasVenta'
import { useAuth } from '../context/AuthContext'
import { descargarArchivo } from '../utils/descargar'
import { etiquetaCliente } from '../utils/cliente'
import { PRESETS_FECHA, rangoPreset, validFechaOpt, fechaToISO, isoToFecha, hoyFecha } from '../utils/fechas'

// Presets de fecha de Deudores: Hoy/Ayer adelante de los de Historia.
const PRESETS_DEUDORES = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'ayer', label: 'Ayer' },
  ...PRESETS_FECHA,
]
import { colors, text, space, sx, font, radius } from '../design/tokens'

/**
 * CLIENTES → Deudores — cuentas por cobrar desde Presea (snapshot FTP DEUDORES.TXT).
 * Deuda NETA por cliente (debe − pagos a cuenta). Alcance: todos / un cliente (lupa)
 * o rango de códigos; filtro de fechas igual que Historia; reportes (cabecera o
 * cabecera+detalle) en PDF/Excel/CSV/TXT + envío por WhatsApp/mail.
 */
export default function DeudoresPage() {
  const { user } = useAuth()
  const [alcance, setAlcance] = useState('todos') // 'todos' | 'cliente' | 'rango'
  // Filtro por vendedor (sug #43): la cartera de UN vendedor. Default = el
  // código del usuario logueado si lo tiene (cada uno ve su cartera de una).
  const [vendedor, setVendedor] = useState(() => user?.codigo_vendedor ? String(user.codigo_vendedor) : '')
  const [vendedores, setVendedores] = useState([])

  const [cliente, setCliente] = useState(null)
  const [pickerAbierto, setPickerAbierto] = useState(false)
  const [codDesde, setCodDesde] = useState('')
  const [codHasta, setCodHasta] = useState('')
  const [desde, setDesde] = useState(hoyFecha())   // predeterminado: hoy
  const [hasta, setHasta] = useState(hoyFecha())
  const [presetActivo, setPresetActivo] = useState('hoy')

  // El selector se llena con los vendedores que TIENEN deuda, no con los
  // usuarios de LGR (`/usuarios/vendedores`): la cartera es del vendedor de
  // Presea y la mayoría no tiene usuario en el sistema — así quedaban 42
  // vendedores con deuda contra 14 opciones en el filtro (26/08/2026).
  // Sin filtro de fechas a propósito: las fechas recortan COMPROBANTES (la
  // pantalla abre en "hoy"), y quien elige vendedor quiere ver toda su cartera
  // vigente. La lista sale del snapshot de deudores completo.
  useEffect(() => {
    clientesApi.deudoresVendedores()
      .then(({ data }) => setVendedores(data?.data || []))
      .catch(() => { /* sin lista, el filtro queda en Todos */ })
  }, [])

  const [resp, setResp] = useState(null)       // dataset completo (clientes con filas) — alcance todos/rango
  const [page, setPage] = useState(1)          // paginación client-side
  const [unCliente, setUnCliente] = useState(null) // detalle (alcance cliente)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [detalle, setDetalle] = useState(null) // modal detalle de una fila del listado
  const [bajando, setBajando] = useState(null)
  const [tipoReporte, setTipoReporte] = useState('cabecera') // 'cabecera' | 'detalle'

  const filtros = () => ({
    desde, hasta,
    ...(alcance === 'rango' ? { cliente_desde: codDesde, cliente_hasta: codHasta } : {}),
    // El filtro de vendedor no aplica al alcance "un cliente" (ahí el cliente
    // ya está elegido a mano; filtrarlo por cartera solo vaciaría el detalle).
    ...(alcance !== 'cliente' && vendedor ? { vendedor } : {}),
  })

  const consultar = async () => {
    setError(null)
    if (!validFechaOpt(desde) || !validFechaOpt(hasta)) return setError('Fechas en formato DD/MM/AAAA.')
    if (alcance === 'cliente' && !cliente) return setError('Elegí un cliente con la lupa.')
    setLoading(true); setResp(null); setUnCliente(null); setPage(1)
    try {
      if (alcance === 'cliente') {
        const { data } = await clientesApi.deudoresDetalle(cliente.codigo_presea, { desde, hasta })
        setUnCliente(data)
      } else {
        // Dataset completo (con comprobantes por cliente): el listado pagina en
        // memoria y el mensaje de WA/mail puede traer toda la consulta con detalle.
        const { data } = await clientesApi.deudoresCompleto(filtros())
        setResp(data)
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo consultar.')
    } finally {
      setLoading(false)
    }
  }

  const aplicarPreset = (key) => {
    const { d, h } = rangoPreset(key)
    setDesde(d); setHasta(h); setPresetActivo(key)
  }

  const elegirCliente = (c) => { setCliente(c); setPickerAbierto(false); setAlcance('cliente') }

  // La fila ya trae sus comprobantes (dataset completo) → modal sin fetch extra.
  const abrirDetalleFila = (fila) =>
    setDetalle({ cod: fila.cod_cli, nombre: fila.nombre, filas: fila.filas || [], total: fila.total })

  const bajarReporte = async (formato) => {
    setBajando(formato)
    try {
      // En modo "un cliente" el reporte siempre trae su detalle (filtrando por su código).
      const params = alcance === 'cliente'
        ? { formato, tipo: 'detalle', desde, hasta, cliente_desde: cliente.codigo_presea, cliente_hasta: cliente.codigo_presea }
        : { formato, tipo: tipoReporte, ...filtros() }
      const ext = formato === 'excel' ? 'xls' : formato
      await descargarArchivo(clientesApi.deudoresExportUrl(params), `deudores_${Date.now()}.${ext}`)
    } catch {
      setError('No se pudo generar el reporte.')
    } finally { setBajando(null) }
  }

  // Toda la consulta como texto, para WhatsApp/mail genérico (vos elegís a quién).
  const consultaTexto = () => {
    if (unCliente) {
      const L = [`DEUDA — ${unCliente.nombre || ''} (Cód. ${unCliente.cod_cli})`, '']
      unCliente.filas.forEach(f => L.push(`${f.fecha}  ${f.concepto}${f.nro_comprobante ? ` Nº ${f.nro_comprobante}` : ''}  $ ${fmtAR(f.monto)}`))
      L.push('', `TOTAL ADEUDADO: $ ${fmtAR(unCliente.total)}`)
      return L.join('\n')
    }
    const L = [
      `DEUDORES — ${desde && hasta ? `${desde} a ${hasta}` : 'todos los movimientos'}`,
      `${resp?.cantidad ?? 0} clientes · Deuda total $ ${fmtAR(resp?.total_general)}`,
      '',
    ]
    ;(resp?.clientes || []).forEach(c => {
      L.push(`${c.cod_cli}  ${c.nombre}  —  Deuda $ ${fmtAR(c.total)}`)
      ;(c.filas || []).forEach(f => L.push(`    ${f.fecha}  ${f.concepto}${f.nro_comprobante ? ` Nº ${f.nro_comprobante}` : ''}  $ ${fmtAR(f.monto)}`))
    })
    return L.join('\n')
  }

  // WhatsApp genérico: abre el selector de contacto con la consulta ya escrita.
  const enviarWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(consultaTexto())}`, '_blank')
  const enviarMail = () => {
    const asunto = unCliente ? `Deuda — ${unCliente.nombre}` : 'Reporte de deudores'
    window.location.href = `mailto:?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(consultaTexto())}`
  }

  const PER = 50
  const clientesAll = resp?.clientes || []
  const cantidad = resp?.cantidad || 0
  const lastPage = Math.max(1, Math.ceil(cantidad / PER))
  const filas = clientesAll.slice((page - 1) * PER, page * PER)
  const hayResultado = resp || unCliente

  return (
    <div>
      <div style={st.headerRow}>
        <div>
          <h2 style={st.title}>Deudores</h2>
          <p style={st.subtitle}>
            Cuentas por cobrar desde Presea (snapshot diario del FTP). Deuda neta: los pagos a cuenta
            ya están descontados. Elegí el alcance y el período, y generá el reporte.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ ...sx.card, padding: space.xl, marginBottom: space.xl }}>
        {/* Alcance */}
        <label style={st.label}>Alcance</label>
        <div style={st.presetRow}>
          {ALCANCES.map(a => (
            <button key={a.key}
              style={{ ...sx.pill, ...(alcance === a.key ? sx.pillActive : {}) }}
              onClick={() => setAlcance(a.key)}>{a.label}</button>
          ))}
        </div>

        {alcance === 'cliente' && (
          <div style={st.clienteRow}>
            <div style={st.clienteBox}>
              {cliente
                ? <span>{etiquetaCliente(cliente)} <span style={{ ...sx.mono, color: colors.textSec }}>({cliente.codigo_presea})</span></span>
                : <span style={{ color: colors.textMeta }}>Ningún cliente seleccionado</span>}
            </div>
            <Boton variante="secondary" onClick={() => setPickerAbierto(true)}>🔍 Buscar cliente</Boton>
          </div>
        )}
        {alcance === 'rango' && (
          <div style={st.rangoRow}>
            <div>
              <label style={st.label}>Cliente desde (código)</label>
              <input style={st.inputMono} inputMode="numeric" placeholder="0" value={codDesde} onChange={e => setCodDesde(e.target.value.replace(/\D/g, ''))} />
            </div>
            <div>
              <label style={st.label}>Cliente hasta (código)</label>
              <input style={st.inputMono} inputMode="numeric" placeholder="9999999" value={codHasta} onChange={e => setCodHasta(e.target.value.replace(/\D/g, ''))} />
            </div>
          </div>
        )}

        {/* Vendedor (sug #43): la cartera de un vendedor. No aplica al alcance
            "un cliente" — ahí el cliente ya está elegido a mano. */}
        {alcance !== 'cliente' && (
          <div style={{ marginTop: space.lg }}>
            <label style={st.label}>Vendedor</label>
            <select
              style={{ ...st.inputMono, fontFamily: 'inherit', minWidth: 240 }}
              value={vendedor}
              onChange={e => setVendedor(e.target.value)}
            >
              <option value="">Todos</option>
              {/* El vendedor propio del usuario puede no tener deuda hoy: se
                  agrega igual para no perder el filtro que trae por defecto. */}
              {vendedor && !vendedores.some(v => String(v.codigo) === String(vendedor)) && (
                <option value={vendedor}>{vendedor} — sin clientes con deuda</option>
              )}
              {vendedores.map(v => (
                <option key={v.codigo} value={v.codigo}>
                  {v.codigo}{v.nombre ? ` — ${v.nombre}` : ''} ({v.clientes})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Fechas (igual que Historia) */}
        <label style={{ ...st.label, marginTop: space.lg }}>Fechas</label>
        <div style={st.presetRow}>
          {PRESETS_DEUDORES.map(p => (
            <button key={p.key}
              style={{ ...sx.pill, ...(presetActivo === p.key ? sx.pillActive : {}) }}
              onClick={() => aplicarPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        <div style={st.fechaRow}>
          <div>
            <label style={st.label}>Fecha desde</label>
            {/* Campo de fecha nativo: editás cada segmento (día/mes/año) sin que
                se corran los dígitos, + calendario. Reemplaza la máscara frágil. */}
            <input type="date" style={st.inputDate}
              value={fechaToISO(desde)} onChange={e => { setDesde(isoToFecha(e.target.value)); setPresetActivo(null) }} />
          </div>
          <div>
            <label style={st.label}>Fecha hasta</label>
            <input type="date" style={st.inputDate}
              value={fechaToISO(hasta)} onChange={e => { setHasta(isoToFecha(e.target.value)); setPresetActivo(null) }} />
          </div>
          <div style={st.btnRow}>
            <Boton variante="primary" onClick={consultar} disabled={loading}>{loading ? 'Consultando…' : 'Consultar'}</Boton>
          </div>
        </div>
        {error && <div style={st.error}>{error}</div>}
      </div>

      {/* KPIs + reportes (cuando hay resultado) */}
      {hayResultado && (
        <div style={{ ...sx.card, padding: space.xl, marginBottom: space.xl }}>
          <div style={st.kpiRow}>
            {unCliente ? (
              <>
                <div style={st.kpi}><div style={st.kpiLbl}>Cliente</div><div style={st.kpiVal}>{unCliente.nombre || `#${unCliente.cod_cli}`}</div></div>
                <div style={st.kpi}><div style={st.kpiLbl}>Saldo adeudado</div><div style={st.kpiValBig}>$ {fmtAR(unCliente.total)}</div></div>
              </>
            ) : (
              <>
                <div style={st.kpi}><div style={st.kpiLbl}>Clientes deudores</div><div style={{ ...st.kpiVal, ...sx.mono }}>{cantidad.toLocaleString('es-AR')}</div></div>
                <div style={st.kpi}><div style={st.kpiLbl}>Deuda total</div><div style={st.kpiValBig}>$ {fmtAR(resp.total_general)}</div></div>
              </>
            )}
          </div>

          <div style={st.toolbar}>
            {!unCliente && (
              <>
                <span style={st.toolbarLbl}>Reporte</span>
                <button style={{ ...sx.pill, ...(tipoReporte === 'cabecera' ? sx.pillActive : {}) }} onClick={() => setTipoReporte('cabecera')}>Cabecera</button>
                <button style={{ ...sx.pill, ...(tipoReporte === 'detalle' ? sx.pillActive : {}) }} onClick={() => setTipoReporte('detalle')}>Cabecera + detalle</button>
                <span style={st.sep} />
              </>
            )}
            <Boton sm variante="secondary" onClick={() => bajarReporte('pdf')} disabled={bajando}>{bajando === 'pdf' ? '…' : '⬇ PDF'}</Boton>
            <Boton sm variante="secondary" onClick={() => bajarReporte('excel')} disabled={bajando}>{bajando === 'excel' ? '…' : '⬇ Excel'}</Boton>
            <Boton sm variante="secondary" onClick={() => bajarReporte('csv')} disabled={bajando}>{bajando === 'csv' ? '…' : '⬇ CSV'}</Boton>
            <Boton sm variante="secondary" onClick={() => bajarReporte('txt')} disabled={bajando}>{bajando === 'txt' ? '…' : '⬇ TXT'}</Boton>
            <span style={{ ...st.toolbarLbl, marginLeft: space.lg }}>Enviar</span>
            <Boton sm variante="send" onClick={enviarWhatsApp}>📱 WhatsApp</Boton>
            <Boton sm variante="send" onClick={enviarMail}>✉ Mail</Boton>
          </div>
        </div>
      )}

      {/* Listado de totales (solo alcance todos/rango) */}
      {resp && (
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <div style={st.resultHeader}>
            <span style={st.resultTitle}>
              {cantidad.toLocaleString('es-AR')} {cantidad === 1 ? 'cliente' : 'clientes'}
              <span style={st.resultMeta}> · {desde && hasta ? `${desde} → ${hasta}` : 'todos los movimientos'}</span>
            </span>
            {lastPage > 1 && (
              <span style={st.pager}>
                <Boton sm variante="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>←</Boton>
                <span style={st.pagerTxt}>{page} / {lastPage}</span>
                <Boton sm variante="ghost" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>→</Boton>
              </span>
            )}
          </div>
          {filas.length === 0 ? (
            <div style={st.empty}>No hay deudores para esos filtros.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={sx.table}>
                <thead>
                  <tr>
                    <th style={sx.th}>Código</th>
                    <th style={sx.th}>Cliente</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Comprob.</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Más antiguo</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Deuda</th>
                    <th style={sx.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.cod_cli} style={st.tr}>
                      <td style={{ ...sx.td, ...sx.mono }}>{f.cod_cli}</td>
                      <td style={sx.td}>{f.nombre}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{f.comprobantes}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{f.desde || '—'}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>$ {fmtAR(f.total)}</td>
                      <td style={{ ...sx.td, textAlign: 'right' }}>
                        <Boton sm variante="ghost" onClick={() => abrirDetalleFila(f)}>Ver →</Boton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detalle de un cliente (alcance cliente) */}
      {unCliente && (
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <div style={st.resultHeader}>
            <span style={st.resultTitle}>{unCliente.filas.length} comprobante{unCliente.filas.length === 1 ? '' : 's'}</span>
          </div>
          <TablaDetalle filas={unCliente.filas} />
        </div>
      )}

      {pickerAbierto && (
        <ModalListaClientes onSelect={elegirCliente} onClose={() => setPickerAbierto(false)} />
      )}

      {detalle && (
        <Modal onClose={() => setDetalle(null)} title={`Deuda · ${detalle.nombre}`} maxWidth="720px">
          <div style={st.detHead}>
            <span style={sx.mono}>Cód. {detalle.cod}</span>
            <span style={st.detTotal}>Total adeudado: <strong style={sx.mono}>$ {fmtAR(detalle.total)}</strong></span>
          </div>
          {detalle.loading ? <div style={st.empty}>Cargando…</div> : <TablaDetalle filas={detalle.filas} />}
        </Modal>
      )}
    </div>
  )
}

function TablaDetalle({ filas }) {
  if (!filas?.length) return <div style={st.empty}>Sin comprobantes impagos.</div>
  return (
    <table style={sx.table}>
      <thead>
        <tr>
          <th style={sx.th}>Fecha</th>
          <th style={sx.th}>Concepto</th>
          <th style={{ ...sx.th, textAlign: 'right' }}>Comprobante</th>
          <th style={{ ...sx.th, textAlign: 'right' }}>Importe</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((d, i) => (
          <tr key={i} style={d.es_credito ? st.trCredito : st.tr}>
            <td style={{ ...sx.td, ...sx.mono }}>{d.fecha}</td>
            <td style={sx.td}>{d.concepto}{d.es_credito && <span style={st.creditoTag}>a cuenta</span>}</td>
            <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{d.nro_comprobante || '—'}</td>
            <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: d.es_credito ? colors.ok : colors.text }}>$ {fmtAR(d.monto)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function fmtAR(n) { return (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const ALCANCES = [
  { key: 'todos', label: 'Todos los clientes' },
  { key: 'cliente', label: 'Un cliente' },
  { key: 'rango', label: 'Rango de clientes' },
]

const st = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  subtitle: { margin: '4px 0 0', fontSize: text.body, color: colors.textSec, maxWidth: 760, lineHeight: 1.5 },
  label: { display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' },

  presetRow: { display: 'flex', gap: space.sm, flexWrap: 'wrap', marginBottom: space.lg },
  clienteRow: { display: 'flex', gap: space.lg, alignItems: 'center', marginBottom: space.sm },
  clienteBox: { flex: 1, padding: `${space.md}px ${space.lg}px`, border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft, fontSize: text.body, color: colors.text },
  rangoRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.lg, marginBottom: space.sm, maxWidth: 420 },
  fechaRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: space.lg, alignItems: 'end' },
  inputMono: { width: '100%', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.mono, outline: 'none', boxSizing: 'border-box' },
  inputDate: { width: '100%', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.mono, color: colors.text, outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
  btnRow: { display: 'flex', justifyContent: 'flex-end' },
  error: { marginTop: space.lg, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, background: colors.errBg, color: colors.err, fontSize: text.sec, fontWeight: 500 },

  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 300px))', gap: space.lg },
  kpi: { padding: `${space.lg}px ${space.xl}px`, border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft },
  kpiLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  kpiVal: { fontSize: text.title, fontWeight: 600, color: colors.text, marginTop: 4 },
  kpiValBig: { fontSize: text.display, fontWeight: 700, color: colors.text, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 4, whiteSpace: 'nowrap' },

  toolbar: { display: 'flex', alignItems: 'center', gap: space.sm, marginTop: space.xl, flexWrap: 'wrap' },
  toolbarLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2 },
  sep: { width: 1, height: 22, background: colors.border, margin: `0 ${space.sm}px` },

  resultHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.border}` },
  resultTitle: { fontSize: text.body, fontWeight: 600, color: colors.text },
  resultMeta: { fontWeight: 400, color: colors.textSec, fontFamily: font.mono },
  pager: { display: 'flex', alignItems: 'center', gap: space.sm },
  pagerTxt: { fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, minWidth: 52, textAlign: 'center' },

  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  trCredito: { height: 44, borderBottom: `1px solid ${colors.borderSoft}`, background: colors.okBg },
  creditoTag: { ...sx.badge, marginLeft: 6, color: colors.textSec, background: colors.bg, border: `1px solid ${colors.border}`, fontWeight: 500 },
  empty: { padding: `${space.xl}px`, textAlign: 'center', color: colors.textMeta, fontSize: text.body },

  detHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.lg, fontSize: text.sec, color: colors.textSec },
  detTotal: { fontSize: text.body, color: colors.text },
}
